import React from 'react';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

interface PaginationResult<T> {
	index: number;
	setIndex: React.Dispatch<React.SetStateAction<number>>;
	currentItem: T | null | undefined;
	isPrevDisabled: boolean;
	isNextDisabled: boolean;
	handlePrev(): void;
	handleNext(): void;
}

export function usePagination<T>(items: T[]): PaginationResult<T> {
	const { onStepChanged } = usePostOfficeUserInteractions();
	const [index, setIndex] = React.useState(0);

	const hasItems = items.length > 0;
	const currentItem = hasItems ? items[index] : null;
	const isFirstItem = index <= 0;
	const isLastItem = index >= items.length - 1;
	const isNextDisabled = !hasItems || isLastItem;
	const isPrevDisabled = !hasItems || isFirstItem;

	const handlePrev = React.useCallback(() => {
		if (isPrevDisabled) {
			return;
		}

		const prev = Math.max(index - 1, 0);

		void onStepChanged({
			interactionIdentifier: 'previous',
			direction: 'previous',
			fromStep: index,
			toStep: prev,
			additionalMetadata: {
				currentPage: index,
				totalPages: items.length,
			},
		});

		setIndex(prev);
	}, [index, isPrevDisabled, items.length, onStepChanged]);

	const handleNext = React.useCallback(() => {
		if (isNextDisabled) {
			return;
		}

		setIndex((cur) => {
			const next = Math.min(cur + 1, items.length - 1);

			onStepChanged({
				interactionIdentifier: 'next',
				direction: 'next',
				fromStep: cur,
				toStep: next,
				additionalMetadata: {
					currentPage: cur,
					totalPages: items.length,
				},
			});

			return next;
		});
	}, [isNextDisabled, items.length, onStepChanged]);

	return {
		index,
		setIndex,
		currentItem,
		isPrevDisabled,
		isNextDisabled,
		handlePrev,
		handleNext,
	};
}
